/**
 * First-party visit tracking + GA4 + Meta Pixel.
 *
 * Reads env vars (REACT_APP_GA4_ID, REACT_APP_META_PIXEL_ID) at runtime — if
 * either is blank we silently skip that channel. Everything is best-effort and
 * non-blocking; analytics failures never affect the user flow.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";

// Stable session id stored for the lifetime of the browser tab.
function getSessionId() {
    try {
        let id = sessionStorage.getItem("lets_session_id");
        if (!id) {
            id = "s_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
            sessionStorage.setItem("lets_session_id", id);
        }
        return id;
    } catch (_) {
        return "s_anon_" + Date.now();
    }
}

let _loaded = { ga4: false, meta: false };

function loadGA4() {
    if (_loaded.ga4 || !GA4_ID) return;
    _loaded.ga4 = true;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", GA4_ID, { send_page_view: false }); // we drive pageview manually
}

function loadMetaPixel() {
    if (_loaded.meta || !META_PIXEL_ID) return;
    _loaded.meta = true;
    /* eslint-disable */
    !function (f, b, e, v, n, t, s) {
        if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
        if (!f._fbq) f._fbq = n; n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
        t = b.createElement(e); t.async = true; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    }(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    window.fbq && window.fbq("init", META_PIXEL_ID);
}

// Best-effort beacon — never blocks the UI.
function sendBeacon(payload) {
    if (!BACKEND_URL) return;
    const url = `${BACKEND_URL}/api/visits/track`;
    const body = JSON.stringify(payload);
    try {
        if (navigator.sendBeacon) {
            const blob = new Blob([body], { type: "application/json" });
            if (navigator.sendBeacon(url, blob)) return;
        }
    } catch (_) { /* noop */ }
    // Fallback (older browsers / strict CSP)
    try {
        const token = localStorage.getItem("lets_token");
        fetch(url, {
            method: "POST",
            body,
            keepalive: true,
            headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        }).catch(() => {});
    } catch (_) { /* noop */ }
}

export function trackPageView(path, referrer) {
    loadGA4(); loadMetaPixel();
    const ref = referrer ?? document.referrer ?? "";
    sendBeacon({
        session_id: getSessionId(),
        path,
        referrer: ref,
        event_type: "pageview",
        screen: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
    });
    if (window.gtag) window.gtag("event", "page_view", { page_path: path, page_location: window.location.href });
    if (window.fbq)  window.fbq("track", "PageView");
}

export function trackEvent(eventType, meta = {}) {
    sendBeacon({
        session_id: getSessionId(),
        path: window.location.pathname,
        referrer: document.referrer || "",
        event_type: eventType,
        event_meta: meta,
    });
    // Map to GA4 + Meta naming where it makes sense.
    if (window.gtag) {
        const gaName = ({
            signup: "sign_up",
            login: "login",
            booking_created: "begin_checkout",
            payment_completed: "purchase",
        })[eventType] || eventType;
        window.gtag("event", gaName, meta);
    }
    if (window.fbq) {
        const fbName = ({
            signup: "CompleteRegistration",
            booking_created: "InitiateCheckout",
            payment_completed: "Purchase",
        })[eventType];
        if (fbName) window.fbq("track", fbName, meta);
    }
}

/** Hook: fires trackPageView() on every route change. */
export function useTrackPageViews() {
    const loc = useLocation();
    const prev = useRef(null);
    useEffect(() => {
        const path = loc.pathname + (loc.search || "");
        if (prev.current === path) return;
        prev.current = path;
        trackPageView(path);
    }, [loc.pathname, loc.search]);
}
